import {
    ArrayOrSingle,
    enumIsEnumValue,
    hideableValueIsHidden,
    IsExactValueType,
    iterableMapToArray,
    objectCreateFromEntries,
    objectGetEntries,
    objectGetKeys,
    objectPick,
    objectRemoveEntries,
    OmitByValue,
    Values
} from "@wix/devzai-utils-common";
import {DoppeActionPriceData, DoppeHideableValue, DoppeListActionSettings} from "../types";
import {RichTextContent} from "@wix/devzai-utils-react";
import {WixImageResource, WixMediaResource, WixVideoResource} from "@wix/devzai-common-wix";
import {BymoPageMedia} from "../bymo-pages/bymo-page-media";
import {DoppeDtoContactMethod} from "../types/doppe-dto-contact-method";
import {DoppeActionFullName} from "../types/doppe-action-full-name";

export const BymoPagePropsContentSettingSpecialSemanticMeaning = {
    Unspecified: 'Unspecified',
} as const;

export type BymoPagePropsContentSettingSpecialSemanticMeaning = Values<typeof BymoPagePropsContentSettingSpecialSemanticMeaning>;

export const BymoPagePropsContentSettingContextDependentSemanticMeaning = {
    /** The primary title or name of the entity. For a product action, this is the product name. For an event action, this is the event title. For a real estate property action, this is the property name. For a link action, this is the link title. */
    EntityTitle: 'EntityTitle',
    /** The publication or creation date of the entity. For a blog post action, this is when the post was published. For an event action, this is the event start date. For a product action, this might be when the product was launched. */
    EntityPublishDate: 'EntityPublishDate',
    /** The descriptive text or content that explains what the entity is about. For a product action, this describes the product features and details. For an event action, this explains what the event is about. Can be plain text or rich formatted content. */
    EntityDescription: 'EntityDescription',
    /** The company or organization name associated with the entity. For a product action, this is the manufacturer or brand. For an event action, this is the event organizer. For a service action, this is the service provider company name. */
    EntityCompany: 'EntityCompany',
    /** The various ways to contact the entity or its provider. For a business action, these are the business contact methods (phone, email, social links). For a person action, these are the person's contact details. Typically includes phone numbers, email addresses, and social media links. */
    EntityContactMethods: 'EntityContactMethods',
    /** A small label or badge text that highlights special status or promotions. For a product action, this could be "SALE", "NEW", "LIMITED EDITION". For an event action, this could be "SOLD OUT", "VIP", "EARLY BIRD". Usually displays prominently on the entity card. */
    EntityRibbon: 'EntityRibbon',
    /** The physical location or address associated with the entity. For a business action, this is the business address. For an event action, this is where the event takes place. For a real estate property action, this is the property address. */
    EntityAddress: 'EntityAddress',
    /** A promotional or discount code that can be applied to the entity. For a product action, this is a coupon code for purchasing the product. For an event action, this is a discount code for event tickets. For a link action pointing to an external site, this is a code that can be used on that site. */
    EntityCouponCode: 'EntityCouponCode',
    /** A URL that links to a dedicated page or external resource for the entity. For a product action, this is the link to the product page on the seller's website. For an event action, this is the link to detailed event information or registration. For a link action, this is the primary destination URL. */
    EntityPageUrl: 'EntityPageUrl',
    /** Visual media (images or videos) associated with the entity. For a product action, these are product photos or demo videos. For an event action, these are event photos, venue pictures, or promotional videos. For a person action, this is the profile photo or videos. Can be single or multiple media items. */
    EntityMedia: 'EntityMedia',
    /** Information about when the entity is scheduled to occur or be available. For an event action, this includes date, time, and duration of the event. For a course action, this includes class schedule and timings. For a limited-time offer action, this is when the offer is valid. */
    EntityScheduleInfo: 'EntityScheduleInfo',
    /** Details about who will be participating in or attending the entity. For an event action, this lists speakers, performers, or guest attendees. For a workshop action, this mentions the instructors. For a meeting action, this lists the participants. */
    EntityParticipantsInfo: 'EntityParticipantsInfo',
    /** How long the entity lasts or takes. For an event action, this is the event duration (e.g., "2 hours", "3 days"). For a course action, this is the course length. For a video action, this is the video runtime. For a service action, this is the service duration. */
    EntityDuration: 'EntityDuration',
    /** The text displayed on the primary action button for the entity. For a product action, this might be "Buy Now", "Add to Cart", "View Product". For an event action, this might be "Register", "Buy Tickets", "RSVP". For a link action, this might be "Visit Website", "Learn More", "Get Started". */
    EntityActionButtonText: 'EntityActionButtonText',
} as const;

export type BymoPagePropsContentSettingContextDependentSemanticMeaning = Values<typeof BymoPagePropsContentSettingContextDependentSemanticMeaning>;

export const BymoPagePropsContentSettingContextIndependentSemanticMeaning = {
    /** A person's professional title or role in their organization. Examples: "Software Engineer", "CEO", "Marketing Director", "Product Designer", "Sales Manager". Used in person profile actions, team member displays, or contact cards. */
    PersonJobTitle: 'PersonJobTitle',
    /** A person's full name or display name. This could be first and last name combined, or a complete name structure. Used in person profile actions, author credits, team member cards, or anywhere a person's identity is displayed. */
    PersonName: 'PersonName',
    /** The main heading text displayed prominently on a page. This is typically the largest, most visible text that introduces the page content. For a link-in-bio page, this might be the user's name or tagline. For an instant page, this is the page's primary headline. Different from page title which is for SEO/browser tabs. */
    PageHeading: 'PageHeading',
    /** The page title used for SEO, browser tabs, and metadata. This is what appears in the browser tab, search engine results, and when sharing the page on social media. Different from page heading which is displayed on the page itself. Should be concise and descriptive for search engines. */
    PageTitle: 'PageTitle',
    /** Hero or banner media displayed prominently at the top of a page. These are the large, eye-catching images or videos that create the first visual impression of the page. For a link-in-bio page, this could be a background image. For an instant page, this is the hero section media. Can be single or multiple media items. */
    PageHeroMedia: 'PageHeroMedia',
    /** A cover or thumbnail image that represents the page in previews and listings. This is the image shown when the page is shared on social media, displayed in page galleries, or used as a preview. Different from hero media which is displayed on the page itself. Typically a single image optimized for sharing. */
    PageCoverMedia: 'PageCoverMedia',
} as const;

export type BymoPagePropsContentSettingContextIndependentSemanticMeaning = Values<typeof BymoPagePropsContentSettingContextIndependentSemanticMeaning>;


export const BymoPagePropsContentSettingSemanticMeaning = {
    ...BymoPagePropsContentSettingContextDependentSemanticMeaning,
    ...BymoPagePropsContentSettingContextIndependentSemanticMeaning
} as const;

export type BymoPagePropsContentSettingSemanticMeaning = Values<typeof BymoPagePropsContentSettingSemanticMeaning>;

export const BymoPagePropsContentSettingType = {
    Text: 'Text',
    ListOfTexts: 'ListOfTexts',
    PriceData: 'PriceData',
    RichText: 'RichText',
    Url: 'Url',
    Phone: 'Phone',
    MultipleMedia: 'MultipleMedia',
    MultipleImages: 'MultipleImages',
    PersonFullName: 'PersonFullName',
    ContactMethods: 'ContactMethods',
    // Supports also render media in a player.
    MultipleBymoPageMedia: 'MultipleBymoPageMedia',
    SingleMedia: 'SingleMedia',
    SingleImage: 'SingleImage',
    SingleVideo: 'SingleVideo',
    Rating: 'Rating',
} as const;

export type BymoPagePropsContentSettingType = Values<typeof BymoPagePropsContentSettingType>;

const SemanticMeaningAllowedSettingTypes = {
    [BymoPagePropsContentSettingSemanticMeaning.EntityTitle]: {
        [BymoPagePropsContentSettingType.Text]: true,
        [BymoPagePropsContentSettingType.RichText]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.EntityDescription]: {
        [BymoPagePropsContentSettingType.Text]: true,
        [BymoPagePropsContentSettingType.RichText]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.PersonJobTitle]: {
        [BymoPagePropsContentSettingType.Text]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.EntityCompany]: {
        [BymoPagePropsContentSettingType.Text]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.EntityScheduleInfo]: {
        [BymoPagePropsContentSettingType.Text]: true,
        [BymoPagePropsContentSettingType.RichText]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.EntityParticipantsInfo]: {
        [BymoPagePropsContentSettingType.Text]: true,
        [BymoPagePropsContentSettingType.RichText]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.EntityDuration]: {
        [BymoPagePropsContentSettingType.Text]: true,
        [BymoPagePropsContentSettingType.RichText]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.EntityRibbon]: {
        [BymoPagePropsContentSettingType.Text]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.EntityPublishDate]: {
        [BymoPagePropsContentSettingType.Text]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.EntityActionButtonText]: {
        [BymoPagePropsContentSettingType.Text]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.EntityAddress]: {
        [BymoPagePropsContentSettingType.Text]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.EntityCouponCode]: {
        [BymoPagePropsContentSettingType.Text]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.EntityPageUrl]: {
        [BymoPagePropsContentSettingType.Url]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.EntityContactMethods]: {
        [BymoPagePropsContentSettingType.ContactMethods]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.PersonName]: {
        [BymoPagePropsContentSettingType.PersonFullName]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.EntityMedia]: {
        [BymoPagePropsContentSettingType.MultipleMedia]: true,
        [BymoPagePropsContentSettingType.MultipleImages]: true,
        [BymoPagePropsContentSettingType.MultipleBymoPageMedia]: true,
        [BymoPagePropsContentSettingType.SingleMedia]: true,
        [BymoPagePropsContentSettingType.SingleImage]: true,
        [BymoPagePropsContentSettingType.SingleVideo]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.PageHeading]: {
        [BymoPagePropsContentSettingType.Text]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.PageTitle]: {
        [BymoPagePropsContentSettingType.Text]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.PageHeroMedia]: {
        [BymoPagePropsContentSettingType.MultipleMedia]: true,
        [BymoPagePropsContentSettingType.MultipleImages]: true,
        [BymoPagePropsContentSettingType.MultipleBymoPageMedia]: true,
        [BymoPagePropsContentSettingType.SingleMedia]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.PageCoverMedia]: {
        [BymoPagePropsContentSettingType.SingleMedia]: true,
        [BymoPagePropsContentSettingType.SingleImage]: true,
    },
} satisfies Record<BymoPagePropsContentSettingSemanticMeaning, Partial<Record<BymoPagePropsContentSettingType, true>>>;

type SemanticMeaningAllowedSettingTypes = typeof SemanticMeaningAllowedSettingTypes;

export namespace BymoPagePropsContentSettingSemanticMeaning {
    export type ResolveAllowedForSettingType<T extends BymoPagePropsContentSettingType> = ({
        [K in keyof SemanticMeaningAllowedSettingTypes]: T extends keyof SemanticMeaningAllowedSettingTypes[K] ? K : never;
    }[keyof SemanticMeaningAllowedSettingTypes]) | BymoPagePropsContentSettingSpecialSemanticMeaning
}


export const BymoPagePropsSpecialContentSettingMetadata = {
    /** 
     * Temporary marker for fields where it's undecided whether they are content or technical fields.
     * Use this when you need more analysis or discussion to determine the field's classification.
     * The field's TypeScript type must already be supported by BymoPagePropsValueSupportedMetadata.
     * Eventually should be replaced with either a proper content setting metadata or marked as Ignored.
     */
    Unknown: 'Unknown',
    /** 
     * Permanent marker for technical fields that should never be considered content.
     * Use this for fields that are technical settings, bindings, display preferences, or configuration.
     * Examples: productProviderId (binding reference), showButton (display toggle), layoutConfig (technical setting).
     * These fields are excluded from all content operations like AI generation, translation, and semantic search.
     */
    Ignored: 'Ignored',
    /** 
     * Temporary marker for fields that appear to be content but whose TypeScript type is not yet supported.
     * Use this when a field looks like content but its type hasn't been added to BymoPagePropsValueSupportedMetadata yet.
     * Once type support is added to BymoPagePropsValueSupportedMetadata, this should be replaced with either 
     * proper content setting metadata or marked as Ignored after determining if it's truly content or technical.
     */
    ToBeConsidered: 'ToBeConsidered',
} as const;

export type BymoPagePropsSpecialContentSettingMetadata = Values<typeof BymoPagePropsSpecialContentSettingMetadata>;

type BymoPagePropsPickPotentialContentSettings<SETTINGS extends {}> = Pick<SETTINGS, keyof OmitByValue<{
    [K in keyof SETTINGS]: BymoPagePropsValueSupportedMetadata<SETTINGS[K]>
}, never>>

export type BymoPagePropsContentSettingsMetadata<
    SETTINGS extends {},
    POTENTIAL_CONTENT_SETTINGS = BymoPagePropsPickPotentialContentSettings<SETTINGS>
> = {
    [K in keyof POTENTIAL_CONTENT_SETTINGS]: BymoPagePropsValueSupportedMetadata<POTENTIAL_CONTENT_SETTINGS[K]> | Exclude<BymoPagePropsSpecialContentSettingMetadata, typeof BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered>;
} & {
    [K in Exclude<keyof SETTINGS, keyof POTENTIAL_CONTENT_SETTINGS>]?: typeof BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered;
}

export type BymoPagePropsValueSupportedMetadata<T, V = DoppeHideableValue.ResolveValueType<T>> =
    IsExactValueType<RichTextContent, V> extends true ? BymoPagePropsContentSettingMetadata<typeof BymoPagePropsContentSettingType.RichText>:
    IsExactValueType<string, V> extends true ? (BymoPagePropsContentSettingMetadata<typeof BymoPagePropsContentSettingType.Text | typeof BymoPagePropsContentSettingType.Url | typeof BymoPagePropsContentSettingType.Phone>) :
    IsExactValueType<string[], V> extends true ? (BymoPagePropsContentSettingMetadata<typeof BymoPagePropsContentSettingType.ListOfTexts>) :
    IsExactValueType<DoppeActionFullName, V> extends true ? (BymoPagePropsContentSettingMetadata<typeof BymoPagePropsContentSettingType.PersonFullName>) :
    IsExactValueType<DoppeDtoContactMethod[], V> extends true ? (BymoPagePropsContentSettingMetadata<typeof BymoPagePropsContentSettingType.ContactMethods>) :
    IsExactValueType<WixImageResource[] | null, V> extends true ? (BymoPagePropsContentSettingMetadata<typeof BymoPagePropsContentSettingType.MultipleImages>) :
    IsExactValueType<ArrayOrSingle<WixMediaResource> | null, V> extends true ? (BymoPagePropsContentSettingMetadata<typeof BymoPagePropsContentSettingType.MultipleMedia>) :
    IsExactValueType<BymoPageMedia[] | null, V> extends true ? (BymoPagePropsContentSettingMetadata<typeof BymoPagePropsContentSettingType.MultipleBymoPageMedia>) :
    IsExactValueType<WixMediaResource[] | null, V> extends true ? (BymoPagePropsContentSettingMetadata<typeof BymoPagePropsContentSettingType.MultipleMedia>) :
    IsExactValueType<WixImageResource | null, V> extends true ? (BymoPagePropsContentSettingMetadata<typeof BymoPagePropsContentSettingType.SingleImage>) :
    IsExactValueType<WixVideoResource | null, V> extends true ? (BymoPagePropsContentSettingMetadata<typeof BymoPagePropsContentSettingType.SingleVideo>) :
    IsExactValueType<WixMediaResource | null, V> extends true ? (BymoPagePropsContentSettingMetadata<typeof BymoPagePropsContentSettingType.SingleMedia>) :
    IsExactValueType<1 | 2 | 3 | 4 | 5, V> extends true ? (BymoPagePropsContentSettingMetadata<typeof BymoPagePropsContentSettingType.Rating>) :
    IsExactValueType<DoppeActionPriceData, V> extends true ? (BymoPagePropsContentSettingMetadata<typeof BymoPagePropsContentSettingType.PriceData>) :
    never;

export type BymoPagePropsContentSettingMetadata<TYPE extends BymoPagePropsContentSettingType> = {
    type: TYPE;
    semanticMeaning: BymoPagePropsContentSettingSemanticMeaning.ResolveAllowedForSettingType<TYPE>;
    deprecated: boolean;
}

export function bymoPagePropsResolveContentSettingsOfTypes<T extends {}> (
    contentSettingsMetadata: BymoPagePropsContentSettingsMetadata<T>,
    types: BymoPagePropsContentSettingType[]
) {
    return objectCreateFromEntries(iterableMapToArray(
        objectGetEntries(contentSettingsMetadata),
        ([settingName, settingMetadata], skip) => {
            if (!enumIsEnumValue(BymoPagePropsSpecialContentSettingMetadata, settingMetadata) && types.includes((settingMetadata as BymoPagePropsValueSupportedMetadata<any>).type)) {
                return [settingName, settingMetadata];
            }

            return skip;
        }
    ))
}

export function bymoPagePropsCreateContentSettingMetadata<TYPE extends BymoPagePropsContentSettingType>(
    type: TYPE,
    semanticMeaning: BymoPagePropsContentSettingMetadata<TYPE>['semanticMeaning'],
    options: {
        deprecated?: boolean
    } = {}
): BymoPagePropsContentSettingMetadata<TYPE> {

    return {
        type: type,
        semanticMeaning: semanticMeaning,
        deprecated: options.deprecated ?? false
    }
}

export function doppeListActionContentSettingsMetadataCreateForListActionWithMainDetails() {
    return {
        mainTitle: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
        mainImage: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.SingleMedia, BymoPagePropsContentSettingSemanticMeaning.EntityMedia),
    } satisfies BymoPagePropsContentSettingsMetadata<DoppeListActionSettings.WithMainDetails>
}

export type BymoPagePropsContentSettingsFilterOptions = {
    visibleOnly: boolean;
    settingTypes: BymoPagePropsContentSettingType[];
}

export function bymoPagePropsFilterContentSettings<T extends {}> (
    settings: T,
    contentSettingsMetadata: BymoPagePropsContentSettingsMetadata<T>,
    options: BymoPagePropsContentSettingsFilterOptions
) {
    const {
        settingTypes,
        visibleOnly
    } = options;

    const filteredSettings = objectPick(
        settings,
        objectGetKeys(bymoPagePropsResolveContentSettingsOfTypes(contentSettingsMetadata, settingTypes)) as any
    );

    if (visibleOnly) {
        return objectRemoveEntries(filteredSettings, value => {
            return hideableValueIsHidden(value);
        })
    } else {
        return filteredSettings;
    }
}

export const DoppeActionTypeContentSpecialSettingsMetadata = {
    Ignored: 'Ignored'
} as const;

export type DoppeActionTypeContentSpecialSettingsMetadata = Values<typeof DoppeActionTypeContentSpecialSettingsMetadata>;